import React from "react";
import { Helmet } from "react-helmet";
import defaultOgImage from "../../assets/banner_2022.jpg";

// Per-page SEO head block (#574 via #596). Emits the document title
// (brand-suffixed), meta description, Open Graph tags, a Twitter card, a
// canonical link when a location is given, and a VideoObject JSON-LD block
// for video pages. Follows the existing per-page Helmet conventions
// (charSet first — see EventPass.tsx). When no image is supplied the main
// brand banner is used so shares always carry an og:image.
//
// Titles are passed UNBRANDED ("Careers", not "FarmFLiX.tv | Careers") —
// the component owns the "| FarmFLiX.tv" suffix.

export type SeoVideo = {
  name: string;
  description: string;
  thumbnailUrl: string;
  // Date, or ISO-8601/Date-parseable string; normalised to ISO for JSON-LD.
  uploadDate?: string | Date;
  // Duration carried by the video entity in milliseconds.
  durationMs?: number;
};

type SeoProps = {
  title: string;
  description: string;
  image?: string;
  // Canonical location: either an absolute `url` (legacy #596 callers) or a
  // site-relative `path` ("/careers"). Omit both for no canonical/og:url.
  url?: string;
  path?: string;
  type?: string;
  video?: SeoVideo;
};

// Unfurlers (Facebook/Twitter/WhatsApp) resolve og:image literally — a
// root-relative bundled asset path ("/static/media/...") is useless to them,
// so anything non-absolute is prefixed with the production origin. No shared
// base-URL constant exists in this codebase (env.js is API-only; existing
// pages hardcode the origin), hence the literal here.
const SITE_ORIGIN = "https://farmflix.tv";

const toAbsoluteUrl = (path: string): string =>
  /^https?:\/\//.test(path)
    ? path
    : `${SITE_ORIGIN}${path.startsWith("/") ? "" : "/"}${path}`;

// Convert a millisecond duration to an ISO-8601 duration (e.g. PT2M30S).
const msToIsoDuration = (ms: number): string => {
  const totalSeconds = Math.max(0, Math.round(ms / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  let iso = "PT";
  if (hours > 0) {
    iso += `${hours}H`;
  }
  if (minutes > 0) {
    iso += `${minutes}M`;
  }
  if (seconds > 0 || (hours === 0 && minutes === 0)) {
    iso += `${seconds}S`;
  }
  return iso;
};

const toIsoDate = (value?: string | Date): string | undefined => {
  if (!value) {
    return undefined;
  }
  const parsed = value instanceof Date ? value : new Date(value);
  return Number.isNaN(parsed.getTime()) ? String(value) : parsed.toISOString();
};

const buildVideoJsonLd = (video: SeoVideo) => {
  const uploadDate = toIsoDate(video.uploadDate);
  return {
    "@context": "https://schema.org",
    "@type": "VideoObject",
    name: video.name,
    description: video.description,
    thumbnailUrl: video.thumbnailUrl,
    ...(uploadDate ? { uploadDate } : {}),
    ...(typeof video.durationMs === "number"
      ? { duration: msToIsoDuration(video.durationMs) }
      : {}),
  };
};

const Seo = ({
  title,
  description,
  image,
  url,
  path,
  type = "website",
  video,
}: SeoProps) => {
  const canonical =
    url || (path !== undefined ? `${SITE_ORIGIN}${path}` : undefined);

  return (
    <Helmet>
      <meta charSet="utf-8" />
      <title>{`${title} | FarmFLiX.tv`}</title>
      <meta name="description" content={description} />
      <meta property="og:site_name" content="FarmFLiX" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta
        property="og:image"
        content={toAbsoluteUrl(image || defaultOgImage)}
      />
      {canonical ? <meta property="og:url" content={canonical} /> : null}
      <meta property="og:type" content={type} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta
        name="twitter:image"
        content={toAbsoluteUrl(image || defaultOgImage)}
      />
      {canonical ? <link rel="canonical" href={canonical} /> : null}
      {video ? (
        <script type="application/ld+json">
          {JSON.stringify(buildVideoJsonLd(video))}
        </script>
      ) : null}
    </Helmet>
  );
};

export default Seo;
